import { inject, Injectable } from '@angular/core';
import { Hex, WordArray, SHA256 } from 'crypto-es';
import {
  CorruptedDataError,
  EnhancedEncryptionService,
} from './enhanced-encryption.service';
import { InsightsService } from '@maximizer/core/shared/insights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { obfuscateStorageValue } from '@maximizer/shared/util';
import { SecureStoragePayload } from '../models/oauth2';

@Injectable()
export class SecureStorageService {
  private readonly encryption = inject(EnhancedEncryptionService);
  private readonly insightsService = inject(InsightsService, { optional: true });

  // Storage configuration constants
  private readonly STORAGE_PREFIX = 'mx_oauth2_';
  private readonly STORAGE_VERSION = '1.0';
  private readonly STORAGE_MAX_AGE_DAYS = 7;
  private readonly HOURS_PER_DAY = 24;

  // Time conversion constants
  private readonly MILLISECONDS_PER_HOUR = 60 * 60 * 1000; // 1 hour in milliseconds

  // Hash and encryption constants
  private readonly TIMESTAMP_BASE36_RADIX = 36; // Base-36 for compact timestamp representation

  // Session ID constants
  private readonly SESSION_ID_BYTES = 16; // 16 bytes = 128 bits for session ID
  private readonly SESSION_ID_HEX_SUBSTRING_LENGTH = 16; // Length of hex substring for session ID

  private get MAX_STORAGE_AGE(): number {
    return (
      this.STORAGE_MAX_AGE_DAYS *
      this.HOURS_PER_DAY *
      this.MILLISECONDS_PER_HOUR
    );
  }

  async storeCredentials<T>(key: string, value: T): Promise<boolean> {
    try {
      const storageKey = this.generateStorageKey(key);
      const storageData = {
        version: this.STORAGE_VERSION,
        timestamp: Date.now(),
        data: value,
        checksum: this.generateChecksum(value),
      };

      const sessionId = this.getSessionId();
      const additionalFactors = [sessionId];
      const masterPassword = this.getMasterPassword();

      const encryptedData = this.encryption.encrypt(
        JSON.stringify(storageData),
        masterPassword,
        additionalFactors,
      );

      const obfuscatedKey = this.obfuscateKey(storageKey);
      localStorage.setItem(obfuscatedKey, encryptedData);

      this.trackTrace('secure-storage-store-success', {
        key,
        storageKey,
        obfuscatedKeyPresent: true,
        sessionIdHash: this.hashSessionId(this.getSessionId()),
      });
      return true;
    } catch {
      this.trackException(
        'secure-storage-store-failed',
        new Error('Failed to store credentials'),
        { key, sessionIdHash: this.hashSessionId(this.getSessionId()) },
      );
      return false;
    }
  }

  async getCredentials<T = unknown>(key: string): Promise<T | null> {
    try {
      const storageKey = this.generateStorageKey(key);
      const obfuscatedKey = this.obfuscateKey(storageKey);
      const encryptedData = localStorage.getItem(obfuscatedKey);
      const sessionId = this.getSessionId();
      const sessionIdHash = this.hashSessionId(sessionId);

      if (!encryptedData) {
        this.trackTrace(
          'secure-storage-miss',
          {
            key,
            storageKey,
            obfuscatedKeyPresent: false,
            sessionIdHash,
          },
          SeverityLevel.Information,
        );
        return null;
      }

      const context = { key, storageKey, sessionId, sessionIdHash };

      const decryptedData = this.decryptStoredData(encryptedData, context);
      if (decryptedData === null) {
        return null;
      }

      return this.parseAndValidateStorageData<T>(decryptedData, context);
    } catch {
      this.trackException(
        'secure-storage-unexpected-retrieval-error',
        new Error('Unexpected retrieval error'),
        { key, sessionIdHash: this.hashSessionId(this.getSessionId()) },
      );
      await this.removeCredentials(key);
      return null;
    }
  }

  private decryptStoredData(
    encryptedData: string,
    context: { key: string; storageKey: string; sessionId: string; sessionIdHash: string },
  ): string | null {
    const traceProps = {
      key: context.key,
      storageKey: context.storageKey,
      obfuscatedKeyPresent: true,
      sessionIdHash: context.sessionIdHash,
    };

    try {
      return this.encryption.decrypt(
        encryptedData,
        this.getMasterPassword(),
        [context.sessionId],
      );
    } catch (error) {
      if (error instanceof CorruptedDataError) {
        this.trackException('secure-storage-corrupted', error, traceProps);
        return null;
      }

      this.trackException('secure-storage-decrypt-failed', error, traceProps);
      return null;
    }
  }

  private parseAndValidateStorageData<T>(
    decryptedData: string,
    context: { key: string; storageKey: string; sessionIdHash: string },
  ): T | null {
    let storageData: SecureStoragePayload<T>;
    try {
      storageData = JSON.parse(decryptedData);
    } catch {
      this.trackTrace('secure-storage-invalid-json', {
        key: context.key,
        storageKey: context.storageKey,
        obfuscatedKeyPresent: true,
        sessionIdHash: context.sessionIdHash,
      });
      return null;
    }

    const validation = this.validateStorageDataIntegrity(storageData);
    if (!validation.valid) {
      this.trackTrace(
        'secure-storage-validation-failed',
        {
          key: context.key,
          storageKey: context.storageKey,
          obfuscatedKeyPresent: true,
          sessionIdHash: context.sessionIdHash,
          reason: validation.reason ?? 'unknown',
          ageMs: validation.ageMs ?? null,
        },
        SeverityLevel.Warning,
      );
      if (validation.reason) {
        void this.removeCredentials(context.key).then((removed) => {
          if (!removed) {
            this.trackTrace('secure-storage-remove-credentials-failed', {
              key: context.key,
              storageKey: context.storageKey,
              sessionIdHash: context.sessionIdHash,
            });
          }
        });
      }
      return null;
    }

    this.trackTrace('secure-storage-read-success', {
      key: context.key,
      storageKey: context.storageKey,
      obfuscatedKeyPresent: true,
      sessionIdHash: context.sessionIdHash,
      ageMs: validation.ageMs ?? null,
    });
    return storageData.data ?? null;
  }

  hasStoredPayload(key: string): boolean {
    try {
      const obfuscatedKey = this.obfuscateKey(this.generateStorageKey(key));
      return localStorage.getItem(obfuscatedKey) !== null;
    } catch {
      return false;
    }
  }

  async removeCredentials(key: string): Promise<boolean> {
    try {
      const storageKey = this.generateStorageKey(key);
      const obfuscatedKey = this.obfuscateKey(storageKey);
      localStorage.removeItem(obfuscatedKey);

      return true;
    } catch {
      return false;
    }
  }

  regenerateSessionId(): void {
    localStorage.removeItem('mx_session_id');
    this.getSessionId();
  }

  private generateStorageKey(key: string): string {
    return `${this.STORAGE_PREFIX}${key}`;
  }

  private obfuscateKey(key: string): string {
    return obfuscateStorageValue(key);
  }

  private generateChecksum(data: unknown): string {
    const str = JSON.stringify(data) ?? '';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.codePointAt(i) ?? 0;
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(this.TIMESTAMP_BASE36_RADIX);
  }

  private validateStorageDataIntegrity(data: unknown): {
    valid: boolean;
    reason?: string;
    ageMs?: number;
  } {
    if (!this.isStoragePayload(data)) {
      return { valid: false, reason: 'invalid-structure' };
    }

    if (data.version !== this.STORAGE_VERSION) {
      return { valid: false, reason: 'version-mismatch' };
    }

    const age = Date.now() - data.timestamp;
    if (age > this.MAX_STORAGE_AGE) {
      return { valid: false, reason: 'expired', ageMs: age };
    }

    const expectedChecksum = this.generateChecksum(data.data);
    if (data.checksum !== expectedChecksum) {
      return { valid: false, reason: 'checksum-mismatch', ageMs: age };
    }

    return { valid: true, ageMs: age };
  }

  private getMasterPassword(): string {
    return `${this.getSessionId()}:maximizer_oauth2_secret`;
  }

  private getSessionId(): string {
    let sessionId = localStorage.getItem('mx_session_id');
    if (!sessionId) {
      sessionId = this.generateSecureSessionId();
      localStorage.setItem('mx_session_id', sessionId);
    }
    return sessionId;
  }

  private generateSecureSessionId(): string {
    // Generate 128-bit random value using crypto-es
    const randomWords = WordArray.random(this.SESSION_ID_BYTES);

    // Convert to hex string and then to base36 for readability
    const randomHex = Hex.stringify(randomWords);
    const randomPart = Number.parseInt(
      randomHex.substring(0, this.SESSION_ID_HEX_SUBSTRING_LENGTH),
      16,
    ).toString(this.TIMESTAMP_BASE36_RADIX);
    const timestampPart = Date.now().toString(this.TIMESTAMP_BASE36_RADIX);

    return randomPart + timestampPart;
  }

  private hashSessionId(value: string): string {
    return SHA256(value).toString();
  }

  private trackTrace(
    message: string,
    properties?: Record<string, unknown>,
    severityLevel: SeverityLevel = SeverityLevel.Verbose,
  ): void {
    if (!this.insightsService) return;
    this.insightsService.trackTrace(
      { message, severityLevel },
      this.withBaseTelemetry({
        ...properties,
        level: SeverityLevel[severityLevel],
      }),
    );
  }

  private trackException(
    event: string,
    error: unknown,
    properties?: Record<string, unknown>,
  ): void {
    if (!this.insightsService) return;
    const exception =
      error instanceof Error ? error : new Error(String(error));
    this.insightsService.trackException(
      { error: exception },
      this.withBaseTelemetry({ event, ...properties }),
    );
  }

  private withBaseTelemetry(
    properties?: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      component: 'SecureStorageService',
      ...properties,
    };
  }

  private isStoragePayload(value: unknown): value is SecureStoragePayload<unknown> {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as SecureStoragePayload<unknown>;
    return (
      typeof candidate.version === 'string' &&
      typeof candidate.timestamp === 'number' &&
      'data' in candidate &&
      typeof candidate.checksum === 'string'
    );
  }
}
